/**
 * Where to send a user whose sign-in could not load.
 *
 * Why this exists (incident 2026-08-27): a customer arrived from a scheduled
 * a11y-scan email, opened their PUBLIC audit report on api.overlayqa.com, then
 * deep-linked into the dashboard and hit a clerk-js ChunkLoadError. The failure
 * card offered nothing but Retry, which only 3 of 28 people had ever pressed
 * (measured 2026-08-05..08-23), so they navigated away and left after 51
 * seconds. The page they had just come from renders with no sign-in at all --
 * that is the accelerator NN/g's efficiency guideline asks for, and it was one
 * link away.
 *
 * SECURITY. The candidate is `document.referrer`, which any site can set by
 * linking to us, and we render it as an anchor on a page that talks about
 * signing in. Emitting it unvalidated is OWASP "Unvalidated Redirects and
 * Forwards" (CWE-601) on the worst possible surface: an attacker's phishing
 * link would wear our origin and our branding. So this is an ALLOW-LIST of our
 * own https origins, never a deny-list, per OWASP's ranked prevention.
 * https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html
 */

/** Origins we will link back to. Exact `URL.origin` match, https only. */
export const ESCAPE_ALLOWED_ORIGINS: readonly string[] = [
  'https://overlayqa.com',
  'https://www.overlayqa.com',
  // Serves the public audit reports and guest review pages, which render with
  // no auth -- the reason this whole module exists.
  'https://api.overlayqa.com',
];

/**
 * Used whenever the referrer is missing or not ours. The marketing site is the
 * right floor: it is public, it does not load clerk-js, and it carries the
 * links a stuck user actually needs.
 */
export const ESCAPE_FALLBACK_URL = 'https://overlayqa.com';

export type EscapeDestination = {
  href: string;
  /** True only when we are sending the user back where they came from. */
  fromReferrer: boolean;
};

const FALLBACK: EscapeDestination = { href: ESCAPE_FALLBACK_URL, fromReferrer: false };

/**
 * Resolve a safe escape destination from a referrer.
 *
 * Deliberately NOT allow-listed: app.overlayqa.com. That is the origin whose
 * sign-in just failed, so linking back to it is a loop, not an escape.
 *
 * `new URL()` does the heavy lifting and is why this stays short: it throws on
 * a protocol-relative or unparseable value, and it normalises backslash and
 * userinfo tricks into an `origin` that simply will not match the list.
 */
export function resolveEscapeDestination(referrer?: string | null): EscapeDestination {
  if (!referrer) return FALLBACK;

  let parsed: URL;
  try {
    parsed = new URL(referrer);
  } catch {
    return FALLBACK;
  }

  // Belt and braces alongside the origin check: a non-https scheme can never be
  // one of ours, and javascript:/data: resolve to an opaque "null" origin that
  // would fail the list anyway.
  if (parsed.protocol !== 'https:') return FALLBACK;
  if (!ESCAPE_ALLOWED_ORIGINS.includes(parsed.origin)) return FALLBACK;

  /*
   * A bare origin is not "the page you came from".
   *
   * Measured in real Chromium 2026-08-27, and it is the reason this guard
   * exists rather than a tidiness point. Chrome's default Referrer-Policy is
   * strict-origin-when-cross-origin, so on the production path -- an https
   * api.overlayqa.com report linking to an https app.overlayqa.com deep link,
   * which is cross-ORIGIN -- the browser sends the ORIGIN ONLY. We therefore
   * never see the report's path in production, and offering "go back to the
   * page you came from" pointing at https://api.overlayqa.com/ would be a
   * promise we cannot keep: that root is not a page a human wants.
   *
   * So a referrer only earns the came-from treatment when it names a specific
   * page. Everything else falls back to the marketing site, which is honest
   * about where it is sending them.
   */
  if (parsed.pathname === '/' || parsed.pathname === '') return FALLBACK;

  return { href: parsed.href, fromReferrer: true };
}
